import { PropsWithChildren, useEffect, useId, useRef, useState } from 'react';

import 'swiper/css';

import { Carousel } from '@meritcircle/ui/components/carousel/carousel';
import { Tile } from '@meritcircle/ui/components/tile/tile';
import { CarouselHoverItemProps, GameCarouselProps } from './game-carousel.types';
import { InView } from 'react-intersection-observer';
import NextLink from 'next/link';

import {
  AspectRatio,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Link,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { LazyImage, PulseLoader, Rating } from '@meritcircle/ui/components';
import { isExternalUrl } from '@meritcircle/ui/utils';
import useTranslation from 'next-translate/useTranslation';
import { ConditionalWrapper } from '@meritcircle/ui/utils/conditional-wrapper';
import { GameVideo } from '../game-video/game-video';
import { useGetReviewsSummaryQuery } from '@/client/graphql/reviews.generated';
import { rem } from 'polished';
import { FEATURE_FLAGS } from '@/config/feature-flags';

const ACTIVATION_THRESHOLD = 500; //milliseconds

export const GameCarousel = ({
  onClick,
  onInView,
  slides,
  ...rest
}: PropsWithChildren<GameCarouselProps>) => {
  const slidesPerView = useBreakpointValue({ base: 2, md: 3.2, xl: 4.2 });
  const slidesPerGroup = useBreakpointValue({ base: 2, md: 3, xl: 4 });

  const [hoveredIndex, setHoveredIndex] = useState<number | undefined>(undefined);
  const hoveredIndexActive = typeof hoveredIndex !== 'undefined';
  const carouselRef = useRef<HTMLDivElement>();

  const carouselBoxBoundingClientRect = carouselRef?.current?.getBoundingClientRect();

  return (
    <Box
      sx={
        hoveredIndexActive
          ? {
              [`.swiper-slide:nth-of-type(${hoveredIndex + 1})`]: {
                zIndex: 3,
              },
            }
          : {}
      }>
      <Carousel
        {...rest}
        slidesPerView={slidesPerView}
        slidesPerGroup={slidesPerGroup}
        ref={carouselRef}
        wrapperProps={{ pb: { base: '2xl', md: '4xl' } }}>
        {slides.map((slide, index) => (
          <Box position="relative" key={slide.tile.href}>
            <InView threshold={0.5} triggerOnce onChange={inView => inView && onInView(index)}>
              <AspectRatio
                ratio={10 / 16}
                sx={{ '> *': { overflow: 'visible !important' } }}
                transition={`transform ${Math.floor(
                  ACTIVATION_THRESHOLD * 1.3,
                )}ms ease-in-out, opacity 300ms ease-in-out`}>
                <Box
                  as={motion.div}
                  onHoverStart={() => {
                    setHoveredIndex(index);
                  }}
                  onHoverEnd={() => {
                    setHoveredIndex(undefined);
                  }}
                  marginLeft="4px"
                  h="100%"
                  w="100%">
                  <Tile
                    h="100%"
                    opacity={hoveredIndexActive ? 0.4 : 1}
                    onClick={() => onClick(index)}
                    {...slide.tile}
                  />
                  <HoverItem
                    onClick={onClick}
                    isActive={index === hoveredIndex}
                    carouselBoxBoundingClientRect={carouselBoxBoundingClientRect}
                    {...slide}
                  />
                </Box>
              </AspectRatio>
            </InView>
          </Box>
        ))}
      </Carousel>
    </Box>
  );
};

const HoverItem = ({
  isActive,
  tile,
  overlay,
  onClick,
  position,
  carouselBoxBoundingClientRect,
}: CarouselHoverItemProps) => {
  // ratio of hover item and carousel item
  const ratio = 1.89;
  const ref = useRef<HTMLDivElement>();
  const [renderLocation, setRenderLocation] = useState<'left' | 'middle' | 'right' | undefined>(
    undefined,
  );
  const moreInfoId = useId();
  const { t } = useTranslation('games');

  const [{ data, fetching }] = useGetReviewsSummaryQuery({
    variables: {
      gameSlug: overlay.gameSlug,
    },
    pause: !isActive || !FEATURE_FLAGS.REVIEWS,
  });

  const averageRating = fetching ? 0 : data?.getReviewsSummary.averageRating || 0;

  useEffect(() => {
    if (ref.current && carouselBoxBoundingClientRect && isActive) {
      const carouselItemRect = ref.current.getBoundingClientRect();
      const overlayOverflowWidth = (carouselItemRect.width * ratio - carouselItemRect.width) / 2;
      const isCompletelyInView =
        carouselItemRect.left - carouselBoxBoundingClientRect.left + carouselItemRect.width <
          carouselBoxBoundingClientRect.width && carouselItemRect.left > 0;

      if (isCompletelyInView) {
        if (carouselItemRect.left - carouselBoxBoundingClientRect.left - overlayOverflowWidth < 0) {
          setRenderLocation('left');
        } else if (
          carouselItemRect.left -
            carouselBoxBoundingClientRect.left +
            carouselItemRect.width +
            overlayOverflowWidth >
          carouselBoxBoundingClientRect.width
        ) {
          setRenderLocation('right');
        } else {
          setRenderLocation('middle');
        }
      }
    }

    if (!isActive) {
      setRenderLocation(undefined);
    }
  }, [isActive, carouselBoxBoundingClientRect, ratio]);

  return (
    <Box ref={ref} zIndex={1} position="absolute" w="100%" h="100%" pointerEvents="none">
      <AnimatePresence>
        {isActive && renderLocation && (
          <Box
            data-testid="game-grid-hover-item"
            pointerEvents="all"
            position="absolute"
            top="50%"
            left={renderLocation === 'left' ? '100%' : renderLocation === 'right' ? '0%' : '50%'}
            width={`${ratio * 100}%`}
            height={{ base: '75%', md: '67%' }}
            transform="translate(-50%, -50%)">
            <motion.div
              style={{ height: '100%', width: '100%' }}
              transition={{ type: 'spring', duration: 0.3 }}
              initial={{
                opacity: 0,
                scale: 0.8,
              }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              exit={{
                opacity: 0,
                scale: 0.8,
              }}>
              <Box
                as={motion.div}
                pos="absolute"
                w="100%"
                h="100%"
                overflow="hidden"
                borderRadius="base"
                bg="black"
                boxShadow="rgba(0, 0, 0, 0.32) 0px 0px 20px 10px">
                <ConditionalWrapper
                  condition={!!tile.href}
                  trueWrapper={children => (
                    <NextLink href={tile.href} passHref legacyBehavior>
                      <Link onClick={() => onClick(position)}>{children}</Link>
                    </NextLink>
                  )}>
                  <Box
                    pos="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    _after={{
                      content: '""',
                      position: 'absolute',
                      top: '0px',
                      left: '0px',
                      width: '100%',
                      height: '100%',
                      background:
                        'linear-gradient(90deg, rgba(0, 0, 0, 0.7) 5.12%, rgba(0, 0, 0, 0.2) 45.12%, rgba(0, 0, 0, 0) 64.08%)',
                      zIndex: 1,
                    }}>
                    {!overlay.mainVideo?.videoUrl && (
                      <LazyImage
                        alt={overlay.inGameImage.alt}
                        src={overlay.inGameImage.src}
                        objectFit="cover"
                        w="100%"
                        h="100%"
                        pos="absolute"
                        left={0}
                        top={0}
                      />
                    )}
                    {overlay.mainVideo?.videoUrl && (
                      <GameVideo
                        title={overlay.title}
                        videoUrl={overlay.mainVideo.videoUrl}
                        videoType={overlay.mainVideo.videoType}
                        h="100%"
                        w="100%"
                      />
                    )}
                  </Box>
                </ConditionalWrapper>
                <Box paddingX="2xl" h="100%" position="absolute" zIndex={2} pointerEvents="none">
                  <Grid h="100%" gridTemplateColumns={'repeat(12, 1fr)'}>
                    <GridItem colSpan={{ base: 12, sm: 7, xl: 5 }} h="100%">
                      <Flex
                        paddingY="2xl"
                        direction="column"
                        justifyContent="center"
                        align="flex-start"
                        minH="100%">
                        {!!overlay.tags.length && (
                          <Flex flexWrap="wrap">
                            {overlay.tags.map((tag, index) => (
                              <Badge key={index} mr="sm" mb="sm" size="xs" {...tag} />
                            ))}
                          </Flex>
                        )}
                        <Heading as="h4" size="h4" mb="space.12" noOfLines={2}>
                          {overlay.title}
                        </Heading>
                        {FEATURE_FLAGS.REVIEWS && (
                          <Flex minH={rem(14)} mb="space.12" alignItems="center">
                            {fetching ? (
                              <PulseLoader />
                            ) : (
                              <Rating
                                rating={averageRating}
                                maxStarsNumber={5}
                                showOnlyStars={!averageRating}
                              />
                            )}
                          </Flex>
                        )}
                        {overlay.description && (
                          <Text mb="md" color="white" size="sm" fontWeight={400} noOfLines={3}>
                            {overlay.description}
                          </Text>
                        )}
                        <Flex pointerEvents="all">
                          {overlay.directPlayLink && (
                            <NextLink href={overlay.directPlayLink} passHref legacyBehavior>
                              <Button
                                as={Link}
                                isExternal={isExternalUrl(overlay.directPlayLink)}
                                mr="md"
                                variant="primary"
                                size="sm">
                                {t('buttons.play-game')}
                              </Button>
                            </NextLink>
                          )}
                          {tile.href && (
                            <NextLink href={tile.href} passHref legacyBehavior>
                              <Button
                                as={Link}
                                onClick={() => onClick(position)}
                                isExternal={isExternalUrl(tile.href)}
                                id={moreInfoId}
                                variant="secondary"
                                size="sm">
                                {t('buttons.more-info')}
                              </Button>
                            </NextLink>
                          )}
                        </Flex>
                      </Flex>
                    </GridItem>
                  </Grid>
                </Box>
              </Box>
            </motion.div>
          </Box>
        )}
      </AnimatePresence>
    </Box>
  );
};
